import { useState } from 'react';

import { ADMIN_API_URL_SUBPATH } from '@/constants';
import {
  axiosInstance,
  INGREDIENT,
  IngredientWithAmount,
  Language,
  ProductQuiz,
  QUIZ,
  useProduct,
} from '@/core';

export const useDuplicateProduct = () => {
  const [isPending, setIsPending] = useState(false);
  const {
    mutations: {
      createMutation,
      updateMutation,
      updateIngredientsMutation,
      createProductQuizMutation,
      createProductQuizAnswerMutation,
    },
  } = useProduct();

  const duplicateProduct = async (
    originalId: number,
    originalName: Language,
    originalData: {
      description?: Language;
      base_price: string;
      image_id?: number;
      group_ids?: number[];
      allergen_ids?: number[];
      tag_ids?: number[];
    },
  ): Promise<number | undefined> => {
    setIsPending(true);
    const productUrl = `${ADMIN_API_URL_SUBPATH}/product`;

    const createResponse = await createMutation.mutateAsync({
      name: {
        cs: originalName.cs ? `copy_${originalName.cs}` : undefined,
        en: originalName.en ? `copy_${originalName.en}` : undefined,
        de: originalName.de ? `copy_${originalName.de}` : undefined,
      },
      description: originalData.description,
      base_price: originalData.base_price,
      status: 'hidden',
      image_id: originalData.image_id,
    });

    const newProductId = createResponse.data?.id;
    if (!newProductId) return undefined;

    // Copy groups, allergens, and tags
    const hasGroups = originalData.group_ids && originalData.group_ids.length > 0;
    const hasAllergens = originalData.allergen_ids && originalData.allergen_ids.length > 0;
    const hasTags = originalData.tag_ids && originalData.tag_ids.length > 0;
    if (hasGroups || hasAllergens || hasTags) {
      await updateMutation.mutateAsync({
        id: newProductId,
        data: {
          group_ids: originalData.group_ids,
          allergen_ids: originalData.allergen_ids,
          tag_ids: originalData.tag_ids,
        },
      });
    }

    // Copy ingredients
    const ingredientsResponse = await axiosInstance.get<IngredientWithAmount[]>(
      `${productUrl}/${originalId}/${INGREDIENT}`,
    );
    const ingredients = ingredientsResponse.data;
    if (ingredients.length > 0) {
      await updateIngredientsMutation.mutateAsync({
        productId: newProductId,
        data: ingredients.map((i) => ({ ingredient_id: i.ingredient.id, amount: i.amount })),
      });
    }

    // Copy quizzes (variants) with their answers
    const quizzesResponse = await axiosInstance.get<ProductQuiz[]>(
      `${productUrl}/${originalId}/${QUIZ}`,
    );
    for (const quiz of quizzesResponse.data) {
      const newQuizResponse = await createProductQuizMutation.mutateAsync({
        productId: newProductId,
        data: {
          name: quiz.name,
          min_choices: quiz.min_choices,
          max_choices: quiz.max_choices ?? null,
          hidden: quiz.hidden,
        },
      });
      const newQuizId = newQuizResponse.data?.id;
      if (!newQuizId) continue;

      for (const answer of quiz.answers) {
        await createProductQuizAnswerMutation.mutateAsync({
          quizId: newQuizId,
          data: {
            name: answer.name,
            kitchen_note: answer.kitchen_note ?? null,
            total_price: answer.total_price ?? null,
            tax_in_percent: answer.tax_value ?? null,
            image_id: answer.image_id ?? null,
            ordering: answer.ordering ?? null,
            max_amount: answer.max_amount ?? null,
            allergens: answer.allergens.map((a) => a.id),
          },
        });
      }
    }

    setIsPending(false);
    return newProductId;
  };

  return { duplicateProduct, isPending };
};
